import { useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./profile-order.css";

const INPUT_TYPE_LABELS = {
  "message:single": "Single choice",
  "message:multi": "Multiple choice",
  "message:input": "Text input",
  "message:picker": "Picker",
  "message:slider": "Slider",
  "message:date": "Date",
  "message:time": "Time",
};

// 6-dot grip glyph — drag-handle indicator, fades in on row hover (see CSS).
function DragGripIcon() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="2" cy="3" r="1.25" fill="currentColor" />
      <circle cx="2" cy="8" r="1.25" fill="currentColor" />
      <circle cx="2" cy="13" r="1.25" fill="currentColor" />
      <circle cx="8" cy="3" r="1.25" fill="currentColor" />
      <circle cx="8" cy="8" r="1.25" fill="currentColor" />
      <circle cx="8" cy="13" r="1.25" fill="currentColor" />
    </svg>
  );
}

function ProfileOrderRow({ item }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="profile-order__item profile-order__item--draggable"
      {...attributes}
      {...listeners}
    >
      <span className="profile-order__grip" aria-hidden="true">
        <DragGripIcon />
      </span>
      <span className="profile-order__title">{item.title}</span>
      {item.inputType && (
        <span className="profile-order__type">
          {INPUT_TYPE_LABELS[item.inputType] || item.inputType}
        </span>
      )}
    </div>
  );
}

// Single-column drag-and-drop reorder list for subject profile fields.
// `items` is an array of { id, title, inputType }; `onChange` receives the
// reordered array. Mirrors the Mobile home page drag pattern.
export default function ProfileOrderList({ items, onChange }) {
  const [dragId, setDragId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const ids = items.map((item) => item.id);

  const onDragEnd = (event) => {
    setDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      onChange(arrayMove(items, oldIndex, newIndex));
    }
  };

  const dragItem = dragId ? items.find((i) => i.id === dragId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(event) => setDragId(event?.active?.id || null)}
      onDragCancel={() => setDragId(null)}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="profile-order__list">
          {items.map((item) => (
            <ProfileOrderRow key={item.id} item={item} />
          ))}
          {items.length === 0 && <div className="profile-order__empty">No profile fields</div>}
        </div>
      </SortableContext>

      <DragOverlay>
        {dragItem ? (
          <div className="profile-order__item profile-order__item--overlay">
            <span className="profile-order__grip profile-order__grip--visible" aria-hidden="true">
              <DragGripIcon />
            </span>
            <span className="profile-order__title">{dragItem.title}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
